import type { LanguageCode } from "config/languages";
import getDomainFromLanguage from "utils/getDomainFromLanguage";
import { getParentLanguage } from "utils/localeUtils";
import { languagesWithoutMagnitudePages, regularPages } from "./config";
import type { RegularPageName } from "./types";

const buildRegularPageUrl = (language: LanguageCode, url: string, isExternal?: boolean): string => {
  const fullSlug = url ? `${url}/` : "";
  const domain = getDomainFromLanguage(language);
  return isExternal ? url : `${domain}/${language}/${fullSlug}`;
};

const passLanguageToBaseUrl = (language: LanguageCode, baseUrl: string): string => {
  const url = new URL(baseUrl);
  url.searchParams.set("lang", language);
  return url.toString();
};

const getRegularPageUrl = (language: LanguageCode, pageName: RegularPageName): string => {
  const page = regularPages[pageName];
  const parentLanguage = getParentLanguage(language);

  const {
    [language]: languageUrl,
    [parentLanguage]: parentLanguageUrl,
    fallback: fallbackUrl,
    en: englishUrl,
    isExternal,
    isMagnitude,
    parentLanguageOnly,
    passLanguageToUrl,
    baseUrl,
  } = page;

  if (languageUrl) return buildRegularPageUrl(language, languageUrl as string, isExternal);

  if (isMagnitude && languagesWithoutMagnitudePages.includes(language))
    return buildRegularPageUrl(parentLanguage, parentLanguageUrl as string, isExternal);

  if (parentLanguageUrl)
    return parentLanguageOnly
      ? buildRegularPageUrl(parentLanguage, parentLanguageUrl as string, isExternal)
      : buildRegularPageUrl(language, parentLanguageUrl as string, isExternal);

  if (passLanguageToUrl && baseUrl) return passLanguageToBaseUrl(language, baseUrl);

  if (fallbackUrl) return buildRegularPageUrl(language, fallbackUrl, isExternal);

  return buildRegularPageUrl("en", englishUrl as string);
};

export default getRegularPageUrl;
